import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  Lock,
  Calendar,
  ShoppingCart,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Flame,
  Sunrise,
  Sun,
  Sunset,
  UtensilsCrossed,
  MoonStar,
  Lightbulb
} from 'lucide-react';
import { useAuth } from '../../common/context/AuthContext';
import PermissionDenied from '../../common/components/PermissionDenied';
import { API_URL } from '../../api/config';
import SessionTimerIndicator from '../settings/settingModal/SessionTimerIndicator';


const PERIOD_CONFIG = {
  breakfast: {
    icon: Sunrise,
    bgClass: 'bg-amber-50',
    iconClass: 'text-amber-500',
    label: 'Breakfast',
  },
  midday: {
    icon: Sun,
    bgClass: 'bg-orange-50',
    iconClass: 'text-orange-500',
    label: 'Midday',
  },
  evening: {
    icon: Sunset,
    bgClass: 'bg-violet-50',
    iconClass: 'text-violet-500',
    label: 'Evening',
  },
  dinner: {
    icon: UtensilsCrossed,
    bgClass: 'bg-rose-50',
    iconClass: 'text-rose-500',
    label: 'Dinner',
  },
};

const getPeriodConfig = (period) =>
  PERIOD_CONFIG[period] ?? {
    icon: MoonStar,
    bgClass: 'bg-slate-100',
    iconClass: 'text-slate-500',
    label: 'Late Night / Others',
  };


const PeakHoursAnalytics = () => {
  const { userToken: token } = useAuth();
  const navigate = useNavigate();

  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [timeRange, setTimeRange] = useState('today');
  const [customDate, setCustomDate] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  const [peakHoursData, setPeakHoursData] = useState(null);
  const [expandedPeriod, setExpandedPeriod] = useState('today');
  const [sortBy, setSortBy] = useState('orders');

  useEffect(() => {
    if (token) checkSubscriptionStatus();
  }, [token]);

  useEffect(() => {
    if (token && subscriptionStatus?.hasAccess) fetchPeakHoursData();
  }, [timeRange, token, subscriptionStatus, customDate]);

  const checkSubscriptionStatus = async () => {
    try {
      setCheckingSubscription(true);
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch subscription status');
      const data = await response.json();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setError(error.message);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const fetchPeakHoursData = async () => {
    try {
      setLoading(true);
      setError(null);

      let queryParams = '';
      if (timeRange === 'custom') {
        const formatDate = (dateStr) => new Date(dateStr).toISOString().split('T')[0];
        queryParams = `startDate=${formatDate(customDate.startDate)}&endDate=${formatDate(customDate.endDate)}`;
      } else {
        queryParams = `timeFrame=${timeRange}`;
      }

      const response = await fetch(`${API_URL}/reports/peak-hours?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied: contact admin for permissions');
        }
        throw new Error('Failed to fetch peak hours data');
      }

      const data = await response.json();
      setPeakHoursData(data);
    } catch (error) {
      console.error('Error fetching peak hours data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = () => {
    window.location.href = '/subscription';
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-red-500 mb-4 text-xl">Authentication Required</div>
          <p className="text-gray-600 mb-4">Please log in to view peak hours analytics.</p>
        </div>
      </div>
    );
  }

  if (checkingSubscription) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600 text-lg">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  if (!subscriptionStatus?.hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="bg-indigo-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Lock className="text-indigo-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Premium Feature</h2>
          <div className="bg-indigo-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700">
              Peak Hours Analytics is a premium feature. Subscribe now or start your free trial to access
              detailed peak hour insights and order patterns.
            </p>
          </div>
          <div className="space-y-3">
            {subscriptionStatus?.canStartTrial && (
              <p className="text-sm text-green-600 font-medium">
                ✓ Free trial available - No credit card required
              </p>
            )}
            <button
              onClick={handleSubscribe}
              className="w-full px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg shadow hover:bg-indigo-700 transition duration-200"
            >
              {subscriptionStatus?.canStartTrial ? 'Start Free Trial' : 'Subscribe Now'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <PermissionDenied
        variant="card"
        title="Error Loading Analytics"
        message={error}
        onRetry={fetchPeakHoursData}
        onGoHome={() => navigate('/')}
        isRetrying={loading}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="min-h-screen bg-slate-50 p-8">
        <SessionTimerIndicator module="expenseReport" />
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="flex justify-between items-center px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Clock className="mr-2" />
              Peak Hours Analytics
            </h1>
            <div className="flex items-center space-x-4">
              {subscriptionStatus?.status === 'trial' && (
                <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  Trial: {subscriptionStatus.daysRemaining} days left
                </div>
              )}
              <button
                onClick={fetchPeakHoursData}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>
        </header>

        <main className="px-6 py-6">
          {/* Time Range Selection */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Calendar size={20} className="mr-2" />
              Select Time Range
            </h2>

            <div className="flex flex-wrap gap-3 mb-4">
              {[
                { label: 'Today', value: 'today' },
                { label: 'Yesterday', value: 'yesterday' },
                { label: 'Last 7 Days', value: 'last7days' },
                { label: 'Last 30 Days', value: 'last30days' },
                { label: 'Last 3 Months', value: 'last3months' },
                { label: 'Custom', value: 'custom' },
              ].map((range) => (
                <button
                  key={range.value}
                  onClick={() => setTimeRange(range.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    timeRange === range.value
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>

            {timeRange === 'custom' && (
              <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                  <input
                    type="date"
                    value={customDate.startDate}
                    onChange={(e) => setCustomDate({ ...customDate, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                  <input
                    type="date"
                    value={customDate.endDate}
                    onChange={(e) => setCustomDate({ ...customDate, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
                <p className="mt-4 text-gray-600">Loading peak hours data...</p>
              </div>
            </div>
          ) : peakHoursData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
                  <h3 className="text-gray-500 text-sm font-medium mb-2">Peak Hour (Overall)</h3>
                  <p className="text-3xl font-bold text-gray-800">
                    {peakHoursData.overall?.peakHour || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">{peakHoursData.overall?.orders || 0} orders</p>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-amber-500">
                  <h3 className="text-gray-500 text-sm font-medium mb-2">Most Delivered Item</h3>
                  <p className="text-2xl font-bold text-gray-800">
                    {peakHoursData.topItem?.itemName || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    {peakHoursData.topItem?.quantity || 0} units sold
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
                  <h3 className="text-gray-500 text-sm font-medium mb-2">Total Orders</h3>
                  <p className="text-3xl font-bold text-gray-800">{peakHoursData.totalOrders || 0}</p>
                  <p className="text-sm text-gray-600 mt-2">Across all hours</p>
                </div>
              </div>

              {/* Peak Hours Breakdown */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                      <Flame className="mr-2 text-red-500" />
                      Peak Hours Breakdown
                    </h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSortBy('orders')}
                        className={`px-3 py-1 rounded text-sm ${
                          sortBy === 'orders' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        By Orders
                      </button>
                      <button
                        onClick={() => setSortBy('revenue')}
                        className={`px-3 py-1 rounded text-sm ${
                          sortBy === 'revenue' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        By Revenue
                      </button>
                    </div>
                  </div>
                </div>

                <div className="divide-y divide-gray-200">
                  {peakHoursData.hourlyBreakdown?.map((period) => {
                    const config = getPeriodConfig(period.period);
                    const PeriodIcon = config.icon;

                    return (
                      <div key={period.period} className="hover:bg-gray-50 transition-colors">
                        <div
                          className="p-6 cursor-pointer"
                          onClick={() =>
                            setExpandedPeriod(expandedPeriod === period.period ? null : period.period)
                          }
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 flex-1">
                              <div className={`${config.bgClass} p-3 rounded-lg`}>
                                <PeriodIcon className={config.iconClass} size={24} strokeWidth={1.75} />
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{config.label}</h3>
                                <p className="text-sm text-gray-500">{period.label}</p>
                              </div>
                            </div>

                            <div className="flex items-center space-x-8">
                              <div className="text-right">
                                <p className="text-sm text-gray-500">Orders</p>
                                <p className="text-2xl font-bold text-gray-900">{period.orders}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-500">Revenue</p>
                                <p className="text-2xl font-bold text-green-600">
                                  ₹{period.revenue?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) || 0}
                                </p>
                              </div>
                              <div>
                                {expandedPeriod === period.period ? (
                                  <ChevronUp className="text-gray-400" size={24} />
                                ) : (
                                  <ChevronDown className="text-gray-400" size={24} />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {expandedPeriod === period.period && (
                          <div className="px-6 pb-6 bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-600 mb-2">Average Order Value</p>
                                <p className="text-2xl font-bold text-gray-900">
                                  ₹{period.avgOrderValue?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) || 0}
                                </p>
                              </div>
                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-600 mb-2">Items Sold</p>
                                <p className="text-2xl font-bold text-gray-900">{period.itemsSold || 0}</p>
                              </div>
                            </div>

                            {period.topItems?.length > 0 && (
                              <div>
                                <h4 className="font-semibold text-gray-800 mb-3">Top Items in this Period</h4>
                                <div className="space-y-2">
                                  {period.topItems.map((item, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center justify-between p-3 bg-white rounded border border-gray-200"
                                    >
                                      <div>
                                        <p className="font-medium text-gray-900">
                                          {idx + 1}. {item.itemName}
                                        </p>
                                        <p className="text-xs text-gray-500">{item.quantity} items sold</p>
                                      </div>
                                      <p className="font-semibold text-indigo-600">{item.quantity}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Most Delivered Items */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <ShoppingCart className="mr-2 text-amber-500" />
                    Most Delivered Items (Ranked)
                  </h2>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">Peak Hour</th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {peakHoursData.topItems?.map((item, index) => (
                        <tr key={item._id} className={index % 2 === 0 ? '' : 'bg-gray-50'}>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-indigo-100 text-indigo-600 font-semibold text-sm">
                              {index + 1}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                            {item.itemName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 font-semibold">
                            {item.totalQuantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-gray-700">
                            {item.peakHour}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-green-600 font-semibold">
                            ₹{item.totalRevenue?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Info Box */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                  <Lightbulb size={20} className="text-blue-600" />
                  Peak Hours Guide
                </h3>
                <ul className="space-y-3 text-sm text-blue-800">
                  {[
                    { icon: Sunrise,        iconClass: 'text-amber-500',  label: 'Breakfast',           time: '6:00 AM – 11:00 AM' },
                    { icon: Sun,            iconClass: 'text-orange-500', label: 'Midday',              time: '11:00 AM – 3:00 PM' },
                    { icon: Sunset,         iconClass: 'text-violet-500', label: 'Evening',             time: '3:00 PM – 6:00 PM' },
                    { icon: UtensilsCrossed,iconClass: 'text-rose-500',   label: 'Dinner',              time: '6:00 PM – 12:00 AM' },
                    { icon: MoonStar,       iconClass: 'text-slate-500',  label: 'Late Night / Others', time: '12:00 AM – 6:00 AM' },
                  ].map(({ icon: Icon, iconClass, label, time }) => (
                    <li key={label} className="flex items-center gap-2">
                      <Icon size={16} className={iconClass} strokeWidth={1.75} />
                      <span><strong>{label}:</strong> {time}</span>
                    </li>
                  ))}
                  <li className="flex items-center gap-2 pt-1 border-t border-blue-200">
                    <Clock size={16} className="text-blue-500" strokeWidth={1.75} />
                    <span>Data is analyzed based on actual order timestamps</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Lightbulb size={16} className="text-blue-500" strokeWidth={1.75} />
                    <span>Use this data to optimize staffing and inventory planning</span>
                  </li>
                </ul>
              </div>
            </>
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default PeakHoursAnalytics;